import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send, Calendar } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    numberOfGuests: '',
    preferredDate: '',
    accommodationName: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const bookings = JSON.parse(localStorage.getItem('bookings') || '[]');
    bookings.push({ ...formData, id: Date.now(), submittedAt: new Date().toISOString() });
    localStorage.setItem('bookings', JSON.stringify(bookings));
    
    toast({
      title: 'Booking Request Submitted!',
      description: 'We will contact you within 24 hours to confirm your balloon safari.',
    });
    
    setFormData({
      fullName: '',
      email: '',
      phone: '',
      numberOfGuests: '',
      preferredDate: '',
      accommodationName: '',
      message: ''
    });
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="relative h-96 flex items-center justify-center text-white"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1761976671459-8a27e6b78c09?w=1200)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(101, 67, 33, 0.5)' }}></div>
        <div className="relative z-10 text-center px-4">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Contact Us</h1>
          <p className="text-xl">Book your balloon safari adventure</p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="text-3xl font-bold text-gray-800 mb-8">
                Get In Touch
              </h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Ready to experience Uganda from above? Contact us to book your 
                balloon safari or for any questions about your upcoming adventure.
              </p>

              <div className="space-y-6">
                <div className="bg-stone-50 p-6 rounded-xl border border-stone-200">
                  <h3 className="font-bold text-gray-800 mb-4 flex items-center">
                    <MapPin className="w-5 h-5 mr-2" style={{ color: '#EE6A1F' }} />
                    LOCATION
                  </h3>
                  <p className="text-gray-600">Uganda National Reserve</p>
                  <p className="text-gray-600">Uganda, East Africa</p>
                </div>

                <div className="bg-stone-50 p-6 rounded-xl border border-stone-200">
                  <h3 className="font-bold text-gray-800 mb-4 flex items-center">
                    <Phone className="w-5 h-5 mr-2" style={{ color: '#EE6A1F' }} />
                    PHONE
                  </h3>
                  <p className="text-gray-600 mb-2">+254 (0) 20 123 4567</p>
                  <p className="text-gray-600">Available 7 AM - 8 PM EAT</p>
                </div>

                <div className="bg-stone-50 p-6 rounded-xl border border-stone-200">
                  <h3 className="font-bold text-gray-800 mb-4 flex items-center">
                    <Mail className="w-5 h-5 mr-2" style={{ color: '#EE6A1F' }} />
                    EMAIL
                  </h3>
                  <p className="text-gray-600">bookings@skyhighadventures.com</p>
                  <p className="text-sm text-gray-500 mt-2">Response within 24 hours</p>
                </div>

                <div className="bg-stone-50 p-6 rounded-xl border-2" style={{ borderColor: '#EE6A1F', borderOpacity: 0.3 }}>
                  <Calendar className="w-8 h-8 mb-3" style={{ color: '#EE6A1F' }} />
                  <h3 className="font-bold text-gray-800 mb-2">Flight Schedule</h3>
                  <p className="text-gray-600 text-sm">
                    Daily flights at sunrise (weather permitting). Advance booking recommended, 
                    especially during peak season (July-October).
                  </p>
                </div>
              </div>
            </div>

            {/* Booking Form */}
            <div>
              <div className="bg-stone-50 p-8 rounded-2xl border-2 border-stone-200 shadow-lg">
                <h2 className="text-3xl font-bold text-gray-800 mb-6">
                  Online Booking Enquiry
                </h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white border-2 border-stone-300 rounded-lg focus:outline-none focus:border-[#EE6A1F]"
                      placeholder="Your full name"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white border-2 border-stone-300 rounded-lg focus:outline-none focus:border-[#EE6A1F]"
                      placeholder="your@email.com"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white border-2 border-stone-300 rounded-lg focus:outline-none focus:border-[#EE6A1F]"
                      placeholder="+254..."
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Number of Guests
                    </label>
                    <select
                      name="numberOfGuests"
                      value={formData.numberOfGuests}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white border-2 border-stone-300 rounded-lg focus:outline-none focus:border-[#EE6A1F]"
                    >
                      <option value="">Select number of guests</option>
                      <option value="1">1 Guest</option>
                      <option value="2">2 Guests</option>
                      <option value="3">3 Guests</option>
                      <option value="4">4 Guests</option>
                      <option value="5+">5+ Guests</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Preferred Date
                    </label>
                    <input
                      type="date"
                      name="preferredDate"
                      value={formData.preferredDate}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white border-2 border-stone-300 rounded-lg focus:outline-none focus:border-[#EE6A1F]"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Accommodation/Lodge Name
                    </label>
                    <input
                      type="text"
                      name="accommodationName"
                      value={formData.accommodationName}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white border-2 border-stone-300 rounded-lg focus:outline-none focus:border-[#EE6A1F]"
                      placeholder="Where are you staying?"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Special Requests / Questions
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows="4"
                      className="w-full px-4 py-3 bg-white border-2 border-stone-300 rounded-lg focus:outline-none focus:border-[#EE6A1F]"
                      placeholder="Any special requirements or questions..."
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    className="w-full text-white font-bold py-4 rounded-lg transition-all flex items-center justify-center shadow-lg hover:opacity-90"
                    style={{ backgroundColor: '#EE6A1F' }}
                  >
                    <Send className="w-5 h-5 mr-2" />
                    SUBMIT BOOKING ENQUIRY
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 text-white text-center" style={{ backgroundColor: '#EE6A1F' }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Begin Your Adventure</h2>
          <p className="text-xl mb-4">
            Experience the magic of Uganda from a perspective few will ever see.
          </p>
          <p className="text-lg">
            Book your balloon safari today and create memories that will last a lifetime.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Contact;
